import React, { useEffect, useRef, useState } from 'react';
import OneSchemaImporter from '@oneschema/react';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import { UploadSimple } from 'phosphor-react';

import withReport from 'components/with-report';
import { REVIEW_DATA_STEP } from 'constants/report-steps';
import { ACCEPTED_FILE_TYPES, HRIS_INTEGRATION_METHOD } from 'constants/common';
import {
  generateOneschemaSessionToken,
  getOneSchemaExcelDownloadUrl,
  retrieveValidatedFile
} from 'api/oneschema';
import { uploadFile, uploadInputFile, updateReport } from 'api/reports';
import { Report } from 'graphql-api';
import ProcessingModal from 'components/processing-modal';
import Spinner from 'components/spinner';
import StepProgressBar from 'components/step-progress-bar';
import DataImportSuccessfullyModal from 'components/review-data-page/data-import-successfully-modal';
import DownloadStartedModal from 'components/review-data-page/download-started-modal';
import { navigateAndStoreNextStep } from 'utils/navigation';
import { downloadFileFromURL } from 'utils/file';

function ReviewDataPage({
  report,
  setReport
}: {
  report: Report;
  setReport: Function;
}): JSX.Element {
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    importFile,
    refreshData,
    downloadStarted
  }: { importFile: boolean; refreshData: boolean; downloadStarted: boolean } = state ?? {};
  const [isOneschemaLoading, setIsOneschemaLoading] = useState(true);
  const [isSessionTokenFetched, setIsSessionTokenFetched] = useState(true);
  const [uploaderKey, setUploaderKey] = useState('');
  const [showProcessingModal, setShowProcessingModal] = useState<boolean>(false);
  const [showDownloadStartedModal, setShowDownloadStartedModal] = useState<boolean>(false);
  const [showImportSuccessfullyModal, setShowImportSuccessfullyModal] = useState<boolean>(
    importFile || refreshData
  );
  const effectCalled = useRef<Boolean>(false);
  const hasOneschemaSessionToken =
    report.oneschemaSessionToken !== undefined &&
    report.oneschemaSessionToken !== null &&
    report.oneschemaSessionToken !== '';

  useEffect(() => {
    if (importFile || refreshData || downloadStarted) {
      window.history.replaceState(null, '', window.location.href);
    }

    if (effectCalled.current === true) return;
    effectCalled.current = true;
    void (async () => {
      const expiredTime =
        report.oneschemaSessionTokenExpiredTime ??
        moment(report.createdAt).add(90, 'days').format();
      if (!hasOneschemaSessionToken || moment().isSameOrAfter(moment(expiredTime))) {
        const updatedReport = await generateOneschemaSessionToken(report);
        setReport(updatedReport);
        setUploaderKey(updatedReport?.oneschemaSessionToken ?? '');
      }
      setIsSessionTokenFetched(false);

      if (downloadStarted) {
        const downloadUrl = await getOneSchemaExcelDownloadUrl(report.id);
        setShowDownloadStartedModal(true);
        downloadFileFromURL(downloadUrl ?? '');
      }
    })();
  }, []);

  function handleReuploadClick(event: React.ChangeEvent<HTMLInputElement>): void {
    void (async () => {
      event.preventDefault();
      const target = event.target as HTMLInputElement;
      const inputFile: File = (target.files as FileList)[0];
      if (ACCEPTED_FILE_TYPES.includes(inputFile.type)) {
        setIsOneschemaLoading(true);
        setIsSessionTokenFetched(true);
        if (report != null) {
          const uploadedReport: Report | null = await uploadInputFile(report, inputFile);
          if (uploadedReport != null) {
            const updatedReport = await generateOneschemaSessionToken(uploadedReport);
            setIsSessionTokenFetched(false);
            if (updatedReport != null) {
              setReport(updatedReport);
              setUploaderKey(updatedReport?.oneschemaSessionToken ?? '');
            } else {
              alert('Upload file unsuccessfully');
            }
          } else {
            alert('Upload file unsuccessfully');
          }
        }
      } else {
        alert('Please upload a CSV or Excel file.');
      }
    })();
  }

  const onOneschemaSuccess = async (data: {
    sheet_id: string;
    embed_id: string;
  }): Promise<void> => {
    setShowProcessingModal(true);
    const { sheet_id: sheetId, embed_id: embedId } = data;
    if (sheetId !== undefined && embedId !== undefined) {
      const validatedFile = await retrieveValidatedFile(report?.id, sheetId, embedId);
      if (report != null && validatedFile != null) {
        const uploadedFileKey: string | null = await uploadFile(
          report,
          validatedFile,
          'validated-file.csv'
        );
        if (uploadedFileKey != null) {
          const updatedReport: Report | null = await updateReport(report, {
            validatedFile: uploadedFileKey
          });
          if (updatedReport != null) {
            await navigateAndStoreNextStep(updatedReport, REVIEW_DATA_STEP, navigate);
          } else {
            setShowProcessingModal(false);
            alert('Import data unsuccessfully');
          }
        } else {
          alert('Import data unsuccessfully');
        }
      } else {
        alert('Import data unsuccessfully');
      }
    } else {
      alert('Import data unsuccessfully');
    }
  };

  const uploadCompleteFileButton = (
    <>
      <label htmlFor="file-input" className="btn-secondary cursor-pointer text-sm text-gray-700">
        <UploadSimple size={16} weight="bold" color="#596D84" className="mr-2.5" /> Upload completed
        file
      </label>
      <input
        type="file"
        accept={ACCEPTED_FILE_TYPES.join(',')}
        id="file-input"
        hidden
        onChange={handleReuploadClick}
      />
    </>
  );

  return (
    <div className="bg-white shadow mt-8 pt-16">
      <div className="px-14">
        <StepProgressBar
          report={report}
          currentStep={REVIEW_DATA_STEP}
          rightElement={uploadCompleteFileButton}
        />
        <div className="text-2xl font-medium leading-6 text-gray-900">
          {report.dataImportMethod === HRIS_INTEGRATION_METHOD
            ? 'Please input the following required fields'
            : 'Review and complete your data'}
        </div>
        <div className="text-base text-gray-700 mt-2 mb-8 2xl:w-2/3">
          Need to complete lots of fields? Click &quot;Export&quot; to download a file to complete
          offline (when you&apos;re done upload it by clicking &quot;Upload completed file&quot;).
          Just need to update a single cell? Double click on it. Want to do bulk updates? Click
          &quot;Find and replace&quot;. Note: EEO-1 job category is an optional field -- feel free
          to include if you have it defined, otherwise we&apos;ll automatically match them for you.
        </div>
        {isOneschemaLoading && (
          <div className="text-center pb-16">
            <Spinner className="text-gray-500" size={48} />
          </div>
        )}
      </div>
      {!isSessionTokenFetched && hasOneschemaSessionToken && (
        <OneSchemaImporter
          key={uploaderKey}
          isOpen={true}
          clientId={process.env.REACT_APP_ONESCHEMA_CLIENT_ID ?? ''}
          sessionToken={report.oneschemaSessionToken ?? ''}
          inline={true}
          className="border-t border-gray-200"
          style={{
            width: '100%',
            height: '70vh'
          }}
          onLaunched={() => {
            setIsOneschemaLoading(false);
          }}
          onSuccess={(data) => {
            void onOneschemaSuccess(data);
          }}
        />
      )}
      {showProcessingModal && <ProcessingModal header="Importing your data..." />}
      {!isOneschemaLoading && showImportSuccessfullyModal && (
        <DataImportSuccessfullyModal
          open={showImportSuccessfullyModal}
          setOpen={setShowImportSuccessfullyModal}
          isRefreshData={refreshData}
        />
      )}
      {showDownloadStartedModal && (
        <DownloadStartedModal
          open={showDownloadStartedModal}
          setOpen={setShowDownloadStartedModal}
        />
      )}
    </div>
  );
}

export default withReport(ReviewDataPage);
